import { IS_MOBILE } from '@blocksuite/global/env';
import { computed, signal } from '@preact/signals-core';
import type { TemplateResult } from 'lit';

import { MenuFocusable } from './focusable.js';
import type { MenuComponentInterface } from './types.js';

export type MenuConfig = (
  menu: Menu,
  index: number
) => TemplateResult | undefined;

export type MenuOptions = {
  onComplete?: () => void;
  onClose?: () => void;
  title?: {
    text: string;
    onBack?: (menu: Menu) => boolean | void;
    onClose?: () => void;
    postfix?: () => TemplateResult;
  };
  search?: {
    placeholder?: string;
  };
  items: MenuConfig[];
  testId?: string;
};

// Global menu open listener type
type MenuOpenListener = (menu: Menu) => (() => void) | void;

// Global menu open listeners
const menuOpenListeners = new Set<MenuOpenListener>();

/**
 * How long the cursor must rest on a submenu item before it takes over an
 * already open submenu (hover-intent, see `Menu.requestSubMenuSwitch`).
 */
export const SUB_MENU_SWITCH_DELAY_MS = 180;

/**
 * How long an open submenu survives after the cursor moves onto a plain item
 * of the parent menu (hover-intent, see `Menu.requestSubMenuClose`). The gap
 * between a menu and its submenu is covered by the parent's own items, so
 * closing immediately would kill the submenu while the cursor is still on its
 * way there.
 */
export const SUB_MENU_CLOSE_DELAY_MS = 180;

// Add global menu open listener
export function onMenuOpen(listener: MenuOpenListener) {
  menuOpenListeners.add(listener);
  // Return cleanup function
  return () => {
    menuOpenListeners.delete(listener);
  };
}

export class Menu {
  private _cleanupFns: Array<() => void> = [];

  private readonly _currentFocused$ = signal<MenuFocusable>();

  private readonly _subMenu$ = signal<Menu>();

  closed = false;

  readonly currentFocused$ = computed(() => this._currentFocused$.value);

  menuElement: MenuComponentInterface;

  searchName$ = signal('');

  searchResult$ = computed(() => {
    return this.renderItems(this.options.items);
  });

  showSearch$ = computed(() => {
    return this.enableSearch && this.searchName$.value.length > 0;
  });

  get enableSearch() {
    return true;
  }

  constructor(public options: MenuOptions) {
    this.menuElement = IS_MOBILE
      ? document.createElement('mobile-menu')
      : document.createElement('affine-menu');
    this.menuElement.menu = this;
    if (this.options.testId) {
      this.menuElement.dataset.testid = this.options.testId;
    }

    // Call global menu open listeners
    menuOpenListeners.forEach(listener => {
      const cleanup = listener(this);
      if (cleanup) {
        this._cleanupFns.push(cleanup);
      }
    });
  }

  close() {
    if (this.closed) {
      return;
    }
    this.closed = true;
    this.cancelSubMenuSwitch();
    this.cancelSubMenuClose();
    // Execute cleanup functions
    this._cleanupFns.forEach(cleanup => cleanup());
    this._cleanupFns = [];

    this.menuElement.remove();
    this.options.onClose?.();
  }

  private _subMenuSwitchTimer: ReturnType<typeof setTimeout> | undefined;
  private _subMenuReenterCancel: (() => void) | undefined;
  private _subMenuCloseTimer: ReturnType<typeof setTimeout> | undefined;
  private _subMenuCloseCancel: (() => void) | undefined;

  closeSubMenu() {
    this.cancelSubMenuSwitch();
    this.cancelSubMenuClose();
    this._subMenu$.value?.close();
    this._subMenu$.value = undefined;
  }

  /**
   * Hover-intent submenu close. Plain items of the parent menu ask for the
   * open submenu to be closed when the cursor reaches them, but the request is
   * delayed by SUB_MENU_CLOSE_DELAY_MS and cancelled as soon as the cursor
   * enters the submenu, so sweeping across a sibling item on the way to the
   * submenu no longer dismisses it.
   */
  requestSubMenuClose() {
    const current = this._subMenu$.value;
    if (!current || this.closed) {
      return;
    }
    // A pending switch targets the same submenu slot; closing supersedes it.
    this.cancelSubMenuSwitch();
    if (this._subMenuCloseTimer != null) {
      return;
    }
    this._subMenuCloseTimer = setTimeout(() => {
      this._subMenuCloseTimer = undefined;
      this._removeSubMenuCloseCancel();
      if (!this.closed) {
        this.closeSubMenu();
      }
    }, SUB_MENU_CLOSE_DELAY_MS);
    const cancel = () => this.cancelSubMenuClose();
    current.menuElement.addEventListener('mouseenter', cancel, { once: true });
    this._subMenuCloseCancel = cancel;
  }

  cancelSubMenuClose() {
    if (this._subMenuCloseTimer != null) {
      clearTimeout(this._subMenuCloseTimer);
      this._subMenuCloseTimer = undefined;
    }
    this._removeSubMenuCloseCancel();
  }

  private _removeSubMenuCloseCancel() {
    if (this._subMenuCloseCancel && this._subMenu$.value) {
      this._subMenu$.value.menuElement.removeEventListener(
        'mouseenter',
        this._subMenuCloseCancel
      );
    }
    this._subMenuCloseCancel = undefined;
  }

  /**
   * Hover-intent submenu switch. When a submenu is already open, taking it
   * over is delayed by SUB_MENU_SWITCH_DELAY_MS and is cancelled if the
   * cursor leaves the item that requested the switch or re-enters the
   * currently open submenu. This keeps a diagonal sweep across a sibling
   * item from stealing an open submenu.
   */
  requestSubMenuSwitch(open: () => void) {
    this.cancelSubMenuSwitch();
    this.cancelSubMenuClose();
    const current = this._subMenu$.value;
    if (!current || this.closed) {
      open();
      return;
    }
    this._subMenuSwitchTimer = setTimeout(() => {
      this._subMenuSwitchTimer = undefined;
      this._removeSubMenuReenterCancel();
      if (!this.closed) {
        open();
      }
    }, SUB_MENU_SWITCH_DELAY_MS);
    const reenterCancel = () => this.cancelSubMenuSwitch();
    current.menuElement.addEventListener('mouseenter', reenterCancel, {
      once: true,
    });
    this._subMenuReenterCancel = reenterCancel;
  }

  cancelSubMenuSwitch() {
    if (this._subMenuSwitchTimer != null) {
      clearTimeout(this._subMenuSwitchTimer);
      this._subMenuSwitchTimer = undefined;
    }
    this._removeSubMenuReenterCancel();
  }

  private _removeSubMenuReenterCancel() {
    if (this._subMenuReenterCancel && this._subMenu$.value) {
      this._subMenu$.value.menuElement.removeEventListener(
        'mouseenter',
        this._subMenuReenterCancel
      );
    }
    this._subMenuReenterCancel = undefined;
  }

  focusNext() {
    if (!this._currentFocused$.value) {
      const ele = this.menuElement.getFirstFocusableElement();
      if (ele instanceof MenuFocusable) {
        ele.focus();
      }
      return;
    }
    const list = this.menuElement
      .getFocusableElements()
      .filter(ele => ele instanceof MenuFocusable);
    const index = list.indexOf(this._currentFocused$.value);
    list[index + 1]?.focus();
  }

  focusPrev() {
    if (!this._currentFocused$.value) {
      return;
    }
    const list = this.menuElement
      .getFocusableElements()
      .filter(ele => ele instanceof MenuFocusable);
    const index = list.indexOf(this._currentFocused$.value);
    if (index === 0) {
      this._currentFocused$.value = undefined;
      return;
    }
    list[index - 1]?.focus();
  }

  focusTo(ele?: MenuFocusable) {
    this.menuElement.focusTo(ele);
  }

  openSubMenu(menu: Menu) {
    this.closeSubMenu();
    this._subMenu$.value = menu;
  }

  pressEnter() {
    this._currentFocused$.value?.onPressEnter();
  }

  renderItems(items: MenuConfig[]) {
    const result = [];
    // oxlint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const template = item(this, result.length);
      if (template != null) {
        result.push(template);
      }
    }
    return result;
  }

  search(name: string) {
    return name.toLowerCase().includes(this.searchName$.value.toLowerCase());
  }

  setFocusOnly(ele?: MenuFocusable) {
    this._currentFocused$.value = ele;
  }
}
